import * as React from 'react';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Helmet } from 'react-helmet-async';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';

import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import useAuthRoles from '../../../hooks/useAuthRoles';
import AdvancedFormValues from '../../../types/AdvancedFormValues';
import AdvancedSettingsResponse from '../../../types/AdvancedSettingsResponse';
import SettingsService from '../../../services/SettingsService/SettingsService';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import {
  RagAiModel,
  AI_MODEL_LABELS,
  DEFAULT_AI_MODEL,
  supportsTemperature
} from '../../../types/RagAiModel';
import {
  SummarisationAiModel,
  SUMMARISATION_AI_MODEL_LABELS
} from '../../../types/SummarisationAiModel';
import {
  HealthScoreAiModel,
  HEALTH_SCORE_AI_MODEL_LABELS
} from '../../../types/HealthScoreAiModel';
import CustomInput from '../../../components/CustomInput/CustomInput';
import CustomTextArea from '../../../components/CustomTextArea/CustomTextArea';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import Button from '../../../components/Button/Button';

function AiSettingsTab(): React.ReactElement {
  const { currentChatbot } = React.useContext(ChatbotContext);
  const { userIsJapetoAdmin } = useAuthRoles();

  const isJapetoAdmin = userIsJapetoAdmin();

  const [settings, setSettings] =
    React.useState<AdvancedSettingsResponse | null>(null);
  const [isLoadingData, setIsLoadingData] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [dataErrorMessage, setDataErrorMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [showError, setShowError] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);

  const validationSchema = Yup.object().shape({
    chatbotPurpose: Yup.string().nullable(),
    styleHints: Yup.string().nullable(),
    aiModel: isJapetoAdmin
      ? Yup.string()
          .oneOf(Object.values(RagAiModel), 'Invalid AI model')
          .required('AI model is required')
      : Yup.string(),
    // Temperature is only validated for models that accept it; the input is hidden otherwise.
    aiTemperature: isJapetoAdmin
      ? Yup.number().when('aiModel', {
          is: (model: string | undefined) => supportsTemperature(model),
          then: (schema) =>
            schema
              .min(0, 'Temperature must be between 0 and 1')
              .max(1, 'Temperature must be between 0 and 1')
              .required('Temperature is required'),
          otherwise: (schema) => schema.notRequired()
        })
      : Yup.number(),
    summarisationAiModel: isJapetoAdmin
      ? Yup.string()
          .oneOf(Object.values(SummarisationAiModel), 'Invalid AI model')
          .required('AI model is required')
      : Yup.string(),
    healthScoreAiModel: isJapetoAdmin
      ? Yup.string()
          .oneOf(Object.values(HealthScoreAiModel), 'Invalid AI model')
          .required('AI model is required')
      : Yup.string()
  });

  const {
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors }
  } = useForm<AdvancedFormValues>({
    resolver: yupResolver(validationSchema)
  });

  const selectedModelSupportsTemperature = supportsTemperature(
    watch('aiModel') || DEFAULT_AI_MODEL
  );

  React.useEffect(() => {
    const controller = new AbortController();

    if (currentChatbot) {
      const fetchSettings = async (): Promise<void> => {
        try {
          const response = await SettingsService.getAdvancedSettings(
            currentChatbot.id,
            controller.signal
          );
          setSettings(response);
          setValue('dataRetentionDays', response.dataRetentionDays);
          setValue('piiEnabled', response.piiEnabled);
          setValue('chatbotPurpose', response.chatbotPurpose);
          setValue('styleHints', response.styleHints);
          setValue('aiModel', response.aiModel);
          setValue('aiTemperature', response.aiTemperature);
          setValue('summarisationAiModel', response.summarisationAiModel);
          setValue('healthScoreAiModel', response.healthScoreAiModel);
        } catch (err: any) {
          if (err.name !== 'CanceledError') {
            setDataErrorMessage('Failed to load settings');
          }
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchSettings();
    } else {
      setIsLoadingData(false);
    }

    return (): void => controller.abort();
  }, [currentChatbot, setValue]);

  const onSubmit = async (data: AdvancedFormValues): Promise<void> => {
    setErrorMessage('');
    setSuccessMessage('');
    setShowError(false);
    setShowSuccess(false);
    if (!currentChatbot || !settings) return;

    // The backend requires a temperature even for models that ignore it, so keep the stored value.
    const aiTemperature = supportsTemperature(data.aiModel)
      ? data.aiTemperature
      : settings.aiTemperature;

    try {
      setIsLoading(true);
      await SettingsService.updateAdvancedSettings(currentChatbot.id, {
        dataRetentionDays: settings.dataRetentionDays,
        piiEnabled: settings.piiEnabled,
        chatbotPurpose: data.chatbotPurpose,
        styleHints: data.styleHints,
        aiModel: data.aiModel,
        aiTemperature,
        summarisationAiModel: data.summarisationAiModel,
        healthScoreAiModel: data.healthScoreAiModel
      });

      setSuccessMessage('Settings updated');
      setShowSuccess(true);
    } catch (err: any) {
      if (err.name !== 'CanceledError') {
        setErrorMessage(err.message || 'Something went wrong');
        setShowError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) return <LoadingSpinner size={200} />;
  if (dataErrorMessage)
    return (
      <NotificationSnackbar
        message={dataErrorMessage}
        severity='error'
        open
        onClose={() => setDataErrorMessage('')}
      />
    );

  return (
    <div className='flex flex-1 flex-col'>
      <Helmet>
        <title>Japeto - AI Settings</title>
      </Helmet>

      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={isJapetoAdmin ? 'w-[56rem] max-w-full' : 'w-full max-w-2xl'}
      >
        <div className='flex flex-col lg:flex-row gap-8 items-start'>
          <div className='w-full lg:w-1/2 space-y-4'>
            <fieldset className='font-medium text-sm border-0 p-0 mb-4'>
              <Controller
                name='chatbotPurpose'
                control={control}
                render={({ field }) => (
                  <CustomTextArea
                    label='Chatbot purpose'
                    tooltip='Describe what the chatbot is for. This guides how the AI responds.'
                    error={errors.chatbotPurpose}
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    placeholder='Describe the main purpose and goals of your chatbot...'
                  />
                )}
              />
            </fieldset>

            <fieldset className='font-medium text-sm border-0 p-0 mb-4'>
              <Controller
                name='styleHints'
                control={control}
                render={({ field }) => (
                  <CustomTextArea
                    label='Writing style'
                    tooltip='Set the tone, language, and length of the AI’s responses.'
                    error={errors.styleHints}
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    className='min-h-32'
                    placeholder='Describe the tone, style, and personality your chatbot should use...'
                  />
                )}
              />
            </fieldset>
          </div>

          {isJapetoAdmin && (
            <div className='w-full lg:w-1/2 space-y-4'>
              <fieldset className='font-medium text-sm border-0 p-0 mb-4 space-y-2'>
                <LabelWithInfo info='The underlying language model used to generate responses.'>
                  AI Model
                </LabelWithInfo>
                <Controller
                  name='aiModel'
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || DEFAULT_AI_MODEL}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select a model' />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(AI_MODEL_LABELS).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.aiModel && (
                  <p className='text-destructive text-sm mt-1'>
                    {errors.aiModel.message}
                  </p>
                )}
              </fieldset>

              <fieldset className='font-medium text-sm border-0 p-0 mb-4 space-y-2'>
                <LabelWithInfo
                  htmlFor='ai-temperature'
                  info='Controls randomness, from 0 (focused and deterministic) to 1 (creative and varied).'
                >
                  Temperature
                </LabelWithInfo>
                {selectedModelSupportsTemperature ? (
                  <Controller
                    name='aiTemperature'
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        id='ai-temperature'
                        type='number'
                        min={0}
                        max={1}
                        step={0.01}
                        value={field.value?.toString() ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ''
                              ? undefined
                              : Number(e.target.value)
                          )
                        }
                        onBlur={field.onBlur}
                        error={errors.aiTemperature}
                        className='w-32'
                        containerClassName='mb-0'
                      />
                    )}
                  />
                ) : (
                  <p className='text-sm text-muted-foreground font-normal'>
                    This model does not accept a temperature setting.
                  </p>
                )}
              </fieldset>
            </div>
          )}
        </div>

        {isJapetoAdmin && (
          <div className='border-t mt-8 pt-6 space-y-4'>
            <h3 className='text-sm font-semibold'>Background AI</h3>
            <fieldset className='w-full lg:w-1/2 font-medium text-sm border-0 p-0 mb-4 space-y-2'>
              <LabelWithInfo
                htmlFor='summarisation-ai-model'
                info='The underlying language model used to summarise conversations.'
              >
                Conversation summarisation model
              </LabelWithInfo>
              <Controller
                name='summarisationAiModel'
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger
                      id='summarisation-ai-model'
                      className='w-full'
                    >
                      <SelectValue placeholder='Select a model' />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SUMMARISATION_AI_MODEL_LABELS).map(
                        ([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.summarisationAiModel && (
                <p className='text-destructive text-sm mt-1'>
                  {errors.summarisationAiModel.message}
                </p>
              )}
            </fieldset>
            <fieldset className='w-full lg:w-1/2 font-medium text-sm border-0 p-0 mb-4 space-y-2'>
              <LabelWithInfo
                htmlFor='health-score-ai-model'
                info='The underlying language model used to analyse conversations for the health score.'
              >
                Health score analysis model
              </LabelWithInfo>
              <Controller
                name='healthScoreAiModel'
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger
                      id='health-score-ai-model'
                      className='w-full'
                    >
                      <SelectValue placeholder='Select a model' />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(HEALTH_SCORE_AI_MODEL_LABELS).map(
                        ([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.healthScoreAiModel && (
                <p className='text-destructive text-sm mt-1'>
                  {errors.healthScoreAiModel.message}
                </p>
              )}
            </fieldset>
          </div>
        )}

        <NotificationSnackbar
          message={successMessage}
          severity='success'
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
        />
        <NotificationSnackbar
          message={errorMessage}
          severity='error'
          open={showError}
          onClose={() => setShowError(false)}
        />
        <div className='flex justify-end mt-3'>
          <Button type='submit' loading={isLoading}>
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AiSettingsTab;
